import { BROADCAST_CHANNELS, CONNECTOR_QUERY_PARAMS, } from '#pplx/frontend/apps/ask/connectors/consts/connectors.js';
import { useAgentHandlerLink } from '#pplx/frontend/apps/ask/connectors/hooks/useAgentHandlerLink.js';
import { useFileConnectorUrl } from '#pplx/frontend/apps/ask/connectors/hooks/useFileConnectorUrl.js';
import { useConnectorActionPageHref } from '#pplx/frontend/apps/ask/connectors/hooks/usePluginsNavigationHrefs.js';
import { useDisconnectConnector } from '#pplx/frontend/apps/ask/hooks/connectors-hooks/useDisconnectConnector.js';
import { postPerplexityConnectorAuthResponse } from '#pplx/frontend/apps/ask/queries/enterprise-queries/postPerplexityConnectorAuthResponse.js';
import { postBlockingEventResponse } from '#pplx/frontend/apps/ask/queries/upsell-queries/postBlockingEventResponse.js';
import { useUserSettingsQuery } from '#pplx/frontend/apps/ask/user-settings/hooks/useUserSettingsQuery.js';
import { openPopup, PopupFailedToOpenError } from '#pplx/frontend/components/utils/popupUtils.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { useCallback, useEffect, useMemo } from 'react';
const parseMergeAuthToken = (token) => {
    if (!token)
        return undefined;
    try {
        const parsed = JSON.parse(token);
        return typeof parsed === 'string' ? parsed : token;
    }
    catch {
        return token;
    }
};
export const useUpsellActionConnectorRuntime = ({ $t, hideUpsell, lastResult, openToast, retryQueryParam, router, upsellInformation, }) => {
    const reason = 'upsell';
    const connectorActionPageHref = useConnectorActionPageHref();
    const { handleConnect } = useFileConnectorUrl({ reason });
    const { mutateAsync: handleDisconnect } = useDisconnectConnector({ reason });
    const { connectors } = useUserSettingsQuery({ reason });
    const mergeAuthToken = upsellInformation?.cta_information?.merge_auth_token;
    const handleMergeAuthConnectorAgentHandlerOnSuccess = useCallback(() => {
        const mergeAuthCallbackUuid = upsellInformation?.cta_information?.merge_auth_callback_uuid;
        if (mergeAuthCallbackUuid) {
            postPerplexityConnectorAuthResponse({
                uuid: mergeAuthCallbackUuid,
                success: true,
                reason: 'Account successfully connected',
            });
        }
        router.push(window.location.pathname + retryQueryParam);
    }, [router, upsellInformation, retryQueryParam]);
    const handleAllowConnectorAuthAgentHandlerOnSuccess = useCallback(() => {
        const backendUUID = upsellInformation?.backend_uuid || lastResult?.backend_uuid;
        postBlockingEventResponse({
            uuid: backendUUID,
            event_type: 'CONNECTOR_AUTH',
            connector_auth_action: true,
        });
    }, [upsellInformation, lastResult?.backend_uuid]);
    const openMergeLink = useAgentHandlerLink({
        linkToken: parseMergeAuthToken(mergeAuthToken),
        onSuccess: () => {
            if (upsellInformation?.cta === 'MERGE_AUTH_CONNECTOR') {
                return handleMergeAuthConnectorAgentHandlerOnSuccess();
            }
            if (upsellInformation?.cta === 'ALLOW_CONNECTOR_AUTH') {
                return handleAllowConnectorAuthAgentHandlerOnSuccess();
            }
        },
    });
    const handleAllowConnectorAuthPopup = useCallback(async (authUpsellInfo) => {
        const connectorAuthUrl = authUpsellInfo.cta_information?.connector_auth_url;
        const backendUUID = authUpsellInfo.backend_uuid || lastResult?.backend_uuid;
        if (!connectorAuthUrl) {
            logger.error('[ALLOW_CONNECTOR_AUTH] Connector auth URL is not defined', {
                upsellInformation: authUpsellInfo,
            });
            return;
        }
        if (!backendUUID) {
            logger.error('[ALLOW_CONNECTOR_AUTH] Backend UUID is not defined', {
                upsellInformation: authUpsellInfo,
            });
            return;
        }
        try {
            await openPopup({
                url: connectorAuthUrl,
                name: '_blank',
                options: '',
            });
            await postBlockingEventResponse({
                uuid: backendUUID,
                event_type: 'CONNECTOR_AUTH',
                connector_auth_action: true,
            });
            hideUpsell?.();
        }
        catch (error) {
            if (error instanceof PopupFailedToOpenError) {
                openToast({
                    message: $t({
                        defaultMessage: 'Failed to open popup window',
                        description: 'Error toast shown when the connector authentication popup window fails to open',
                        id: '1569f79c61',
                    }),
                    variant: 'error',
                    timeoutSeconds: null,
                });
            }
            logger.error('[ALLOW_CONNECTOR_AUTH] Connector auth failed', {
                upsellInformation: authUpsellInfo,
                error,
            });
        }
    }, [lastResult?.backend_uuid, hideUpsell, $t, openToast]);
    const handleAllowConnectorAuthMerge = useCallback(async (authUpsellInfo) => {
        if (!authUpsellInfo.cta_information?.merge_auth_token) {
            logger.error('[ALLOW_CONNECTOR_AUTH] Merge auth token is not defined', {
                upsellInformation: authUpsellInfo,
            });
            openToast({
                message: $t({
                    defaultMessage: 'Failed to start authentication',
                    description: 'Error toast shown when the merge authentication token is missing for connector auth',
                    id: '26bf72ab84',
                }),
                variant: 'error',
                timeoutSeconds: null,
            });
            return;
        }
        openMergeLink();
    }, [openToast, $t, openMergeLink]);
    const handleAllowConnectorAuthAction = useCallback(async (authUpsellInfo) => {
        const isMergeAuth = Boolean(authUpsellInfo.cta_information?.merge_auth_token);
        if (isMergeAuth) {
            return handleAllowConnectorAuthMerge(authUpsellInfo);
        }
        return handleAllowConnectorAuthPopup(authUpsellInfo);
    }, [handleAllowConnectorAuthMerge, handleAllowConnectorAuthPopup]);
    useEffect(() => {
        if (!(import.meta.env.VITE_IS_SIDECAR_BUILD || import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD))
            return;
        const channel = new BroadcastChannel(BROADCAST_CHANNELS.UPSELL_CONNECTOR);
        channel.onmessage = (event) => {
            if (event.data.type === CONNECTOR_QUERY_PARAMS.CONNECTOR_COMPLETE) {
                window.focus();
                redirectWindow(window.location.pathname + retryQueryParam, 'Connector complete');
            }
        };
        return () => {
            channel.close();
        };
    }, [retryQueryParam]);
    return useMemo(() => ({
        connectors: connectors?.connectors,
        connectorActionPageHref,
        handleAllowConnectorAuthAction,
        handleConnect,
        handleDisconnect,
        openMergeLink,
    }), [
        connectors?.connectors,
        connectorActionPageHref,
        handleAllowConnectorAuthAction,
        handleConnect,
        handleDisconnect,
        openMergeLink,
    ]);
};
//# sourceMappingURL=useUpsellActionConnectorRuntime.js.map